import { PICKUP_STATUS_LABEL, PICKUP_VERIFY_PATH, type PickupVerification } from '@clim/domain';
import { useEffect, useState } from 'react';

const day = (iso: string) => iso.split('-').reverse().join('/');
const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });

/**
 * Page publique, sans compte : le fournisseur vérifie un bon d'enlèvement (QR code ou numéro + code) avant de remettre
 * le matériel. Seul le contenu affiché ici fait foi : un bon modifié ne correspond plus, un bon récupéré, annulé ou
 * expiré est signalé « NON VALABLE ».
 */
export function VerifyPickupPage() {
  const slug = decodeURIComponent(window.location.pathname.slice(PICKUP_VERIFY_PATH.length).replace(/^\/+|\/+$/g, ''));
  const params = new URLSearchParams(window.location.search);
  const [company, setCompany] = useState(slug);
  const [number, setNumber] = useState(params.get('n') ?? '');
  const [code, setCode] = useState(params.get('c') ?? '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<PickupVerification | 'not_found' | 'error' | null>(null);
  const check = async (e = company, n = number, c = code) => {
    if (!e.trim() || !n.trim() || !c.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`/v1/public/pickup-orders/verify?${new URLSearchParams({ e: e.trim(), n: n.trim(), c: c.trim() })}`);
      setResult(r.ok ? ((await r.json()) as PickupVerification) : r.status === 404 || r.status === 400 ? 'not_found' : 'error');
    } catch {
      setResult('error');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (slug && params.get('n') && params.get('c')) void check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <main className="portal verify-pickup">
      <h1>Vérification d’un bon d’enlèvement</h1>
      <p>Avant de remettre le matériel, vérifiez que le bon est authentique et encore valable. Seul le contenu affiché ici fait foi.</p>
      <form
        className="form"
        onSubmit={(ev) => {
          ev.preventDefault();
          void check();
        }}
      >
        {!slug && (
          <label>
            Identifiant de l’entreprise (imprimé sur le bon, après « verifier-bon/ »)
            <input className="input" value={company} onChange={(e) => setCompany(e.target.value)} />
          </label>
        )}
        <label>
          Numéro du bon
          <input className="input" aria-label="Numéro du bon" value={number} placeholder="BE-2026-00012" onChange={(e) => setNumber(e.target.value)} />
        </label>
        <label>
          Code de vérification
          <input className="input mono" aria-label="Code de vérification" value={code} placeholder="8 caractères" onChange={(e) => setCode(e.target.value)} />
        </label>
        <button className="btn primary" disabled={busy}>
          Vérifier
        </button>
      </form>
      {result === 'not_found' && (
        <div className="verify-result ko" role="alert">
          <h2>❌ Bon introuvable</h2>
          <p>Aucun bon ne correspond à ce numéro et à ce code. Ne remettez pas le matériel et contactez l’entreprise.</p>
        </div>
      )}
      {result === 'error' && (
        <div className="verify-result ko" role="alert">
          <p>Vérification impossible pour le moment (connexion). Réessayez dans un instant.</p>
        </div>
      )}
      {result && typeof result === 'object' && (
        <div className={`verify-result ${result.valid ? 'ok' : 'ko'}`} role="status" data-testid="verify-result">
          <h2>
            {result.valid
              ? '✅ BON VALABLE'
              : `❌ NON VALABLE — ${result.status === 'issued' && result.expired ? 'expiré' : PICKUP_STATUS_LABEL[result.status].toLowerCase()}`}
          </h2>
          <p>
            <strong>{result.company}</strong> · bon n° <strong>{result.number}</strong>
          </p>
          <p>
            Fournisseur : <strong>{result.supplier}</strong>
            <br />
            Enlevé par : <strong>{result.technician ?? 'technicien de l’entreprise'}</strong>
            <br />
            Chantier : {result.client}
            {result.siteCity ? ` — ${result.siteCity}` : ''}
          </p>
          <p>Matériel à remettre (comparez avec le bon présenté) :</p>
          <ul>
            {result.lines.map((l, k) => (
              <li key={k}>
                <strong>{l.quantity}</strong> × {l.label}
              </li>
            ))}
          </ul>
          {result.notes && <p>Précisions : {result.notes}</p>}
          <p className="small">
            Émis le {when(result.issuedAt)} · valable jusqu’au {day(result.validUntil)}
            {result.pickedUpAt ? ` · matériel déjà récupéré le ${when(result.pickedUpAt)}` : ''}
            {result.cancelledAt ? ` · annulé le ${when(result.cancelledAt)}` : ''}
          </p>
          <p className="small muted">Empreinte : {result.contentHash.slice(0, 32)}…</p>
        </div>
      )}
    </main>
  );
}
